/// <reference types="vite-plugin-svgr/client" />
import LoadingModal from '@components/atoms/modals/LoadingModal';
import { getEmailFromToken, useVerify } from '@hooks/api';
import { useReauthParamsURL } from '@hooks/useReauthParams';
import { useRedirect } from '@hooks/useRedirect';
import { authErrorCode, login, reauth } from '@services/analytics';
import { useAppDispatch } from '@store/index';
import { setLoginPageError } from '@store/reducers/errorMessages/loginPageErrors';
import { setEmail } from '@store/reducers/profile';
import {
    AnalyticsAuthErrorCode,
    AnalyticsAuthMethod,
    AnalyticsAuthStage,
    listOf,
    PermissionScope,
    SecretType,
    TwoFactor,
    VerifyAuthRequest,
} from 'mh-client';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

const VerifyMagicLink = () => {
    const [searchParams] = useSearchParams();
    const queryToken = searchParams.get('token');
    const navigate = useNavigate();
    const intl = useIntl();
    const { url, isReauth } = useReauthParamsURL();
    const fireAuth = isReauth ? reauth : login;
    const dispatch = useAppDispatch();
    const hasCalledVerify = useRef(false);

    useRedirect(!!queryToken, '/login');

    const { mutateAsync } = useVerify();

    useEffect(() => {
        if (queryToken && !hasCalledVerify.current) {
            hasCalledVerify.current = true;
            const scopes = isReauth
                ? listOf([PermissionScope.basic, PermissionScope.security])
                : listOf([PermissionScope.basic]);

            mutateAsync(
                new VerifyAuthRequest(
                    undefined, // email - not needed for magic link
                    SecretType.magicLink,
                    queryToken,
                    scopes
                )
            )
                .then(data => {
                    if (data.accessToken) {
                        if (!isReauth && data.twoFactorRequired) {
                            dispatch(setEmail(getEmailFromToken() ?? ''));
                            if (data.twoFactorRequired === TwoFactor.totp) {
                                navigate('/login/totp');
                            } else {
                                navigate('/login/passkey');
                            }
                        } else {
                            fireAuth({
                                method: AnalyticsAuthMethod.MagicLink,
                                stage: AnalyticsAuthStage.Success,
                            });
                            navigate(url);
                        }
                    }
                })
                .catch((error: unknown) => {
                    fireAuth({
                        method: AnalyticsAuthMethod.MagicLink,
                        stage: AnalyticsAuthStage.Failed,
                        error_code: authErrorCode(
                            error,
                            AnalyticsAuthErrorCode.InvalidMagicLink
                        ),
                    });
                    dispatch(
                        setLoginPageError(
                            intl.formatMessage({
                                id: 'login_magic_link_expired',
                            })
                        )
                    );
                    navigate('/login');
                });
        }
    }, [
        queryToken,
        isReauth,
        mutateAsync,
        dispatch,
        navigate,
        url,
        fireAuth,
        intl,
    ]);

    return <LoadingModal />;
};

export default VerifyMagicLink;
